/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';
import { onlyText } from 'react-children-utilities';

import { Link } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import PageBanner from 'bundles/unified-common/components/PageBanner';

type ComponentProps<T> = T extends React.Component<infer P> ? P : never;

type Props = {
  promoBannerId?: string | null;
  promoBannerText?: string | null;
};

/* eslint-disable camelcase */
const LINK_TRACKING_MAP: { [key: string]: string } = {
  for_universities: 'universities_cta',
  for_college_students: 'student_upswell_cta',
} as const;
/* eslint-enable camelcase */

export const getTrackedLinkRenderer = (trackingName: string) => (props: ComponentProps<TrackedA>) => {
  const { href, children } = props;

  const normalizedTrackingName = trackingName.replace(/-/g, '_');

  const normalizedSlug = onlyText(children).toLowerCase().replace(/\s/g, '_');
  const presetTrackingName = LINK_TRACKING_MAP[normalizedSlug];
  const linkTrackingName = presetTrackingName ?? 'banner_link';

  return (
    <Link
      {...props}
      component={TrackedA}
      trackingName={`${normalizedTrackingName}_${linkTrackingName}`}
      data={{ href }}
    >
      {children}
    </Link>
  );
};

const FrontPageSkinnyBanner = ({ promoBannerId, promoBannerText }: Props) => {
  const trackingName = promoBannerId || 'lohp_cms_banner';
  const linkRenderer = useMemo(() => getTrackedLinkRenderer(trackingName), [trackingName]);

  if (!promoBannerText) {
    return null;
  }

  return (
    <PageBanner
      bannerId={trackingName}
      hasPersistentDismiss={false}
      message={
        <Markdown
          disallowedTypes={['paragraph']}
          unwrapDisallowed
          renderers={{
            link: linkRenderer,
          }}
          source={promoBannerText}
        />
      }
    />
  );
};

export default FrontPageSkinnyBanner;
